import React, { Component } from 'react';

import { Loading } from 'components/cosmos';
import { OfferToast } from 'components/offerToast';
import { parseMediaUrl } from 'helpers/File';
import { pick, get } from 'helpers/Object';
import { rawTextLength } from 'helpers/String';
import { withShellContext } from 'hoc/withShellContext';
import differenceWith from 'lodash/differenceWith';
import intersectionWith from 'lodash/intersectionWith';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import { inject } from 'mobx-react';
import { ContentBasePage } from 'modules/content';
import ClassesService from 'modules/content/classes/ClassesService';
import ContentPreventLeave from 'modules/content/components/ContentPreventLeave';
import { isVideo } from 'modules/file/helpers';
import SalesService from 'modules/salesManager/SalesService';
import { PropTypes } from 'prop-types';

import { SalesInfoFreeSignUp } from './components/freeSignUp';
import { SalesInfoPageConfig } from './components/pageConfig';
import { SalesInfoPrice } from './components/price';

import { Form } from '../../../components/form';
import { UiService } from '../../ui';
import { MAX_DESCRIPTION_LENGTH } from '../constants';

import './SalesInfo.scss';

const SNAPSHOT_PICKED_PROPS = [
  'description',
  'mediaVideoUrl',
  'media',
  'selectedOffers',
  'offers',
  'freeSignUp'
];

@inject((stores) => ({
  offers: stores.SalesStore.offers,
  showableOfferAlert: stores.SalesStore.showableSalesOfferAlert
}))
class SalesInfo extends Component {
  static propTypes = {
    offers: PropTypes.array,
    showableOfferAlert: PropTypes.bool,
    fixedBarId: PropTypes.string,
    isMembership: PropTypes.bool
  };

  state = {
    loading: true,
    currencyCode: null,
    description: '',
    mediaVideoUrl: null,
    media: null,
    image: null,
    selectedOffers: [],
    productId: null,
    offers: [],
    classes: null,
    allowUncheck: false,
    freeSignUp: false,
    snapshot: null
  };

  constructor(props) {
    super(props);

    this.formNode = React.createRef();
    this.offersRef = React.createRef();
  }

  get currentSnapshot() {
    return pick(this.state, SNAPSHOT_PICKED_PROPS);
  }

  get freeSignUpMaskClass() {
    const { freeSignUp } = this.state;

    return !freeSignUp ? 'sales_info__grid__mask' : '';
  }

  async componentDidMount() {
    const { isMembership } = this.props;

    if (isMembership) {
      await SalesService.getOffers();
    }

    await SalesService.fetchSalesOfferAlert();
    await this.fetchData();
    await this.fetchClasses();
    this.setSelectedOffers();
    this.setIfCanUncheck();
    await this.getMediasIds();
    this.filterMediaByPriority();
    this.fixNotEmbedUrl();
    this.disableLoader();
    this.setSnapshot();
  }

  handleAlertClose = SalesService.sendSalesOfferEvent;

  filterMediaByPriority() {
    const { media, mediaVideoUrl } = this.state;

    if (media) {
      this.setState({ mediaVideoUrl: null, image: null });
    } else if (mediaVideoUrl) {
      this.setState({ image: null });
    }
  }

  fixNotEmbedUrl() {
    let { mediaVideoUrl } = this.state;

    if (mediaVideoUrl) {
      const videoObj = parseMediaUrl(mediaVideoUrl);

      if (videoObj.type === 'YOUTUBE') {
        mediaVideoUrl = `https://www.youtube.com/embed/${videoObj.id}`;
      } else if (videoObj.type === 'VIMEO') {
        mediaVideoUrl = `https://player.vimeo.com/video/${videoObj.id}`;
      }

      this.setState({ mediaVideoUrl });
    }
  }

  setMedia(dt) {
    const type = dt.image && !isEmpty(dt.image) ? 'image' : 'media';
    const medias = type === 'image' ? dt.image : dt.media;
    let media = null;

    if (isArray(medias)) {
      media = medias[0];
    } else if (medias) {
      media = {
        id: medias
      };
    }

    this.setState({
      image: null,
      media: null,
      ...{ [type]: media }
    });
  }

  setSnapshot = () => {
    this.setState({ snapshot: this.currentSnapshot });
  };

  async getMediasIds() {
    const { productId } = this.state;

    if (productId) {
      const dt = await SalesService.getMediasIds(productId);

      this.setMedia(dt);
    }
  }

  handleSubmit = async () => {
    const { media } = this.state;

    if (await this.updateData()) {
      if (!media) {
        this.setState({ media: null, image: null });
      } else if (isVideo(media)) {
        this.setState({ media, image: null });
      } else {
        this.setState({ image: media });
      }
    }
  };

  updateData = async () => {
    const {
      description,
      selectedOffers,
      receivedSelectedOffers,
      mediaVideoUrl,
      freeSignUp,
      media
    } = this.state;

    try {
      if (!this.validateDescription()) {
        throw 'The Description exceeds the limit of characters.';
      }

      this.enableLoader();

      await SalesService.updateSalesInfo({
        media,
        description,
        selectedOffers,
        receivedSelectedOffers,
        mediaVideoUrl,
        freeSignUp
      });

      this.setSnapshot();

      this.disableLoader();

      return true;
    } catch (error) {
      this.disableLoader();

      return false;
    }
  };

  validateDescription() {
    const { description } = this.state;

    if (rawTextLength(description) <= MAX_DESCRIPTION_LENGTH) {
      return true;
    }

    UiService.setAlertError('sales-info.description_limit', {
      limit: MAX_DESCRIPTION_LENGTH
    });

    return false;
  }

  setSelectedOffers() {
    const { classes } = this.state;
    let { selectedOffers } = this.state;

    let { offers } = this.props;

    if (classes) {
      offers = SalesService.addClass(offers, classes);
    }

    selectedOffers = intersectionWith(
      selectedOffers,
      offers,
      SalesService.isOfferMatch
    );
    offers = SalesService.formatOffers(offers);

    this.setState({ offers, selectedOffers });
  }

  async fetchClasses() {
    const classes = await SalesService.getClasses();
    const formatedClasses = ClassesService.formatClasses(classes);

    this.setState({ classes: formatedClasses });
  }

  async fetchData() {
    const data = await SalesService.getSalesInfoData();

    if (data) {
      this.setState({
        currencyCode: data.currencyCode || '',
        description: data.description || '',
        mediaVideoUrl: data.mediaVideoUrl || '',
        selectedOffers: data.offers || [],
        receivedSelectedOffers: data.offers,
        productId: data.productId || '',
        freeSignUp: data.freeSignUp
      });
    }
  }

  enableLoader() {
    this.setState({ loading: true });
  }

  disableLoader() {
    this.setState({ loading: false });
  }

  onContentChange = (e) => {
    const { value } = e.target;

    this.setState({ description: value });
  };

  setMediaUrl = (mediaVideoUrl) => {
    this.setState({ mediaVideoUrl });
  };

  setFreeSignUp = ({ target }) => this.setState({ freeSignUp: target.value });

  setIfCanUncheck() {
    const { offers } = this.state;
    const allowUncheck = SalesService.hasElWithProperty(
      offers[Object.keys(offers)[0]],
      'periodicity'
    );

    this.setState({ allowUncheck });
  }

  onUncheckItem = (selectedItem) => {
    let { selectedOffers } = this.state;

    selectedOffers = selectedOffers.filter(
      (item) => item.offerKey !== selectedItem.offerKey
    );

    this.setState({ selectedOffers });
  };

  onCheckItem = (selectedItem, name) => {
    const { offers } = this.state;
    let { selectedOffers } = this.state;

    if (name && offers[name] && isArray(offers[name])) {
      selectedOffers = differenceWith(
        selectedOffers,
        offers[name],
        SalesService.isOfferMatch
      );
    } else {
      selectedOffers = differenceWith(
        selectedOffers,
        offers,
        SalesService.isOfferMatch
      );
    }

    selectedOffers.push(selectedItem);

    this.setState({ selectedOffers });
  };

  handleScrollToOffers = () => {
    this.offersRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };

  handleMediaChange = (event) => {
    const media = get(event, 'target.value', null);

    this.setState({ media });
  };

  render() {
    const {
      description,
      loading,
      mediaVideoUrl,
      media,
      image,
      selectedOffers,
      offers,
      allowUncheck,
      freeSignUp,
      snapshot
    } = this.state;
    const { showableOfferAlert } = this.props;

    if (loading) {
      return <Loading center />;
    }

    return (
      <Form
        className="sales_info"
        ref={this.formNode}
        onSubmit={this.handleSubmit}
      >
        {showableOfferAlert && (
          <OfferToast.Sales
            onScrollToOffers={this.handleScrollToOffers}
            onClose={this.handleAlertClose}
          />
        )}

        <SalesInfoFreeSignUp
          checked={freeSignUp}
          onChange={this.setFreeSignUp}
        />

        {freeSignUp && (
          <>
            <SalesInfoPageConfig
              description={description}
              onContentChange={this.onContentChange}
              media={media}
              image={image}
              mediaVideoUrl={mediaVideoUrl}
              setMediaUrl={this.setMediaUrl}
              onError={this.onError}
              className={this.freeSignUpMaskClass}
              handleMediaChange={this.handleMediaChange}
            />

            <div ref={this.offersRef}>
              <SalesInfoPrice
                selectedOffers={selectedOffers}
                offers={offers}
                allowUncheck={allowUncheck}
                onUncheckItem={this.onUncheckItem}
                onCheckItem={this.onCheckItem}
                className={this.freeSignUpMaskClass}
              />
            </div>
          </>
        )}

        <ContentBasePage.Footer onlySave />

        <ContentPreventLeave
          snapshot={snapshot}
          current={this.currentSnapshot}
          onConfirm={this.updateData}
        />
      </Form>
    );
  }
}

export default withShellContext(SalesInfo);
